import { axios, blueApi, baseUrlForService } from '@/axios.config';
import { parseGrpcData } from '@alphauslabs/grpc-chunk-parser';

const defaultSettings = { global_enabled: false };
const defaultChannels = { data: [] };

export const state = {
  settings: defaultSettings,
  channels: defaultChannels,
  alerts: [],
};

export const actions = {
  deleteNotificationChannel: async ({ dispatch }, id) => {
    try {
      await blueApi.delete(`/admin/v1/notification/channels/${id}`);
      await dispatch('fetchNotificationChannels');
    } catch (e) {
      console.error(e);
    }
  },
  fetchNotificationSettings: async ({ commit }) => {
    try {
      const res = await axios.get('/notification/settings');
      commit('updateNotificationSettings', {
        globalEnabled: res.data.global_enabled,
      });
    } catch (e) {
      console.error(e);
      commit('updateNotificationSettings', defaultSettings);
    }
  },
  saveNotificationSettings: async ({ dispatch }, { globalEnabled }) => {
    try {
      await axios.post('/notification/settings', {
        global_enabled: globalEnabled,
      });
    } catch (e) {
      console.error(e);
    }
    await dispatch('fetchNotificationSettings');
  },
  fetchNotificationChannels: async ({ commit }) => {
    try {
      const res = await axios.get('/notification/channels');
      const data = res.data.data.map(item => ({
        id: item.id,
        mspId: item.msp_id,
        name: item.name,
        type: item.type,
        enabled: item.enabled,
        emailFormat: item.email.format,
        emails: item.email.tos,
        msteamsWebhookUrl: item.msteams.webhook_url,
        slackChannel: item.slack.channel,
      }));
      commit('updateNotificationChannels', data);
    } catch (e) {
      console.error(e);
      commit('updateNotificationChannels', defaultChannels);
    }
  },
  saveNotificationChannels: async ({ dispatch }, { id, ...args }) => {
    try {
      const params = {
        name: args.name,
        type: args.type,
        enabled: args.enabled,
        email_format: args.emailFormat,
        emails: args.emails,
        msteams_webhook_url: args.msteamsWebhookUrl,
        slack_code: args.slackCode,
        slack_redirect_uri: args.slackRedirectUri,
      };
      if (id) {
        await axios.put(`/notification/channels/${id}`, params);
      } else {
        await axios.post('/notification/channels', params);
      }
    } catch (e) {
      console.error(e);
    }
    await dispatch('fetchNotificationChannels');
  },
  postAccountNotifications: async (_, payload) => {
    try {
      await blueApi.post('/admin/v1/notifications', payload);
    } catch (e) {
      console.error(e);
    }
  },
  postNotificationAndBudgetAlert: async ({ commit }, payload) => {
    try {
      const { data } = await blueApi.post('/cost/v1/alerts', payload);
      commit('addNewAlert', data);
    } catch (e) {
      console.error(e);
    }
  },
  deleteAlert: async ({ commit }, id) => {
    try {
      await blueApi.delete(`/cost/v1/alerts/${id}`);
      commit('removeAlert', id);
    } catch (e) {
      console.error(e);
    }
  },
  putNotificationAndBudgetAlert: async ({ commit }, { id, payload }) => {
    try {
      const { data } = await blueApi.put(`/cost/v1/alerts/${id}`, payload);
      commit('removeAlert', id);
      commit('addNewAlert', data);
    } catch (e) {
      console.error(e);
    }
  },
  fetchAlerts: async ({ commit }) => {
    try {
      const alerts = [];
      parseGrpcData(
        {
          url: baseUrlForService('blue') + '/cost/v1/alerts:read',
          method: 'POST',
          headers: {
            Authorization: blueApi.defaults.headers.common['Authorization'],
          },
        },
        {
          concatData: false,
          showDebug: false,
        },
        async data => {
          alerts.push(...data);
          commit('updateAlerts', alerts);
        },
        err => {
          throw err;
        }
      );
    } catch (e) {
      console.error(e);
    }
  },
};

export const mutations = {
  updateNotificationSettings(state, data) {
    state.settings = data;
  },
  updateNotificationChannels(state, data) {
    state.channels.data = data;
  },
  updateAlerts(state, data) {
    state.alerts = data;
  },
  addNewAlert(state, data) {
    state.alerts = [...state.alerts, data];
  },
  removeAlert(state, id) {
    state.alerts = state.alerts.filter(alert => alert.id !== id);
  },
};

export const getters = {
  notificationSettings(state) {
    return state.settings;
  },
  notificationChannels(state) {
    return state.channels.data;
  },
  activeNotificationChannels(state) {
    return state.channels.data.filter(item => Boolean(item.enabled));
  },
  alerts(state) {
    return state.alerts.map(alert => ({
      ...alert,
      channels: alert.notification.channels,
      daily: alert.daily.value + ' $',
      dailyRate: alert.dailyRate.value + ' %',
      monthly: alert.monthly.value + ' $',
    }));
  },
};
